import { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface DeleteAdminDialogProps {
  userId: string;
  userName: string;
  userEmail: string;
  onDeleteComplete: () => void;
}

interface DeletionPreview {
  users: number;
  legacy_users_warning?: number;
  training_modules: number;
  training_sessions: number;
  teams: number;
  candidates: number;
  guards: number;
  missions: number;
  reports: number;
  behavior_records: number;
  emergency_reports: number;
  warning_message?: string;
}

export const DeleteAdminDialog = ({
  userId,
  userName,
  userEmail,
  onDeleteComplete,
}: DeleteAdminDialogProps) => {
  const [step, setStep] = useState<"preview" | "confirm">("preview");
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const loadPreview = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("preview_admin_deletion", {
        admin_user_id: userId,
      });

      if (error) {
        console.error("RPC Error:", error);
        throw error;
      }

      console.log("Preview data:", data);

      // Check if the function returned an error in the data
      if (data && typeof data === 'object' && 'error' in data) {
        throw new Error(data.error as string);
      }

      setPreview(data as unknown as DeletionPreview);
      setStep("preview");
    } catch (error: any) {
      console.error("Error loading deletion preview:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to load deletion preview",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "delete") {
      toast({
        title: "Confirmation Required",
        description: 'Please type "delete" to confirm',
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      // Delete in specific order to avoid constraint violations
      
      // 1. Delete user_roles
      await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      // 2. Delete admin_teams
      await supabase
        .from('admin_teams')
        .delete()
        .eq('admin_id', userId);

      // 3. Delete admin_contributors (both as parent and contributor)
      await supabase
        .from('admin_contributors')
        .delete()
        .eq('contributor_admin_id', userId);
      
      await supabase
        .from('admin_contributors')
        .delete()
        .eq('parent_admin_id', userId);

      // 4. Delete from Supabase Auth (allows email to be reused)
      try {
        await supabase.auth.admin.deleteUser(userId);
      } catch (authError) {
        console.log('Auth deletion failed (may not have permissions):', authError);
      }

      // 5. Delete all admin-related data
      await supabase.from('training_modules').delete().eq('created_by', userId);
      await supabase.from('certificate_templates').delete().eq('created_by', userId);
      await supabase.from('training_categories').delete().eq('created_by', userId);
      await supabase.from('teams').delete().eq('created_by', userId);

      // 6. Delete the profile completely
      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (deleteError) {
        console.log('Error deleting profile:', deleteError);
        // If delete fails due to triggers, do soft delete as last resort
        await supabase
          .from('profiles')
          .update({ 
            email: `deleted_${userId}@deleted.com`,
            full_name: 'Deleted User'
          })
          .eq('id', userId);
      }

      toast({
        title: "Success",
        description: "Admin deleted successfully",
      });

      // Close dialog and refresh immediately
      setStep("preview");
      setConfirmText("");
      setPreview(null);
      onDeleteComplete();
    } catch (error: any) {
      console.error("Error deleting admin:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete admin",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setStep("preview");
    setConfirmText("");
    setPreview(null);
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={loadPreview}
          disabled={isLoading}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent className="max-w-2xl">
        {step === "preview" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Admin: {userName}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left space-y-4">
                <p className="font-semibold">
                  This action will permanently delete:
                </p>

                {preview?.warning_message && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      {preview.warning_message}
                    </p>
                  </div>
                )}

                {preview && (
                  <div className="space-y-2 bg-muted p-4 rounded-md">
                    {preview.users > 0 && (
                      <div className="flex justify-between">
                        <span>Users (including cascaded):</span>
                        <Badge variant="destructive">{preview.users}</Badge>
                      </div>
                    )}
                    {preview.teams > 0 && (
                      <div className="flex justify-between">
                        <span>Teams:</span>
                        <Badge variant="destructive">{preview.teams}</Badge>
                      </div>
                    )}
                    {preview.guards > 0 && (
                      <div className="flex justify-between">
                        <span>Guards:</span>
                        <Badge variant="destructive">{preview.guards}</Badge>
                      </div>
                    )}
                    {preview.training_modules > 0 && (
                      <div className="flex justify-between">
                        <span>Training Modules:</span>
                        <Badge variant="destructive">
                          {preview.training_modules}
                        </Badge>
                      </div>
                    )}
                    {preview.training_sessions > 0 && (
                      <div className="flex justify-between">
                        <span>Training Sessions:</span>
                        <Badge variant="destructive">
                          {preview.training_sessions}
                        </Badge>
                      </div>
                    )}
                    {preview.candidates > 0 && (
                      <div className="flex justify-between">
                        <span>Candidates:</span>
                        <Badge variant="destructive">{preview.candidates}</Badge>
                      </div>
                    )}
                    {preview.missions > 0 && (
                      <div className="flex justify-between">
                        <span>Missions:</span>
                        <Badge variant="destructive">{preview.missions}</Badge>
                      </div>
                    )}
                    {preview.reports > 0 && (
                      <div className="flex justify-between">
                        <span>Reports:</span>
                        <Badge variant="destructive">{preview.reports}</Badge>
                      </div>
                    )}
                    {preview.behavior_records > 0 && (
                      <div className="flex justify-between">
                        <span>Behavior Records:</span>
                        <Badge variant="destructive">
                          {preview.behavior_records}
                        </Badge>
                      </div>
                    )}
                    {preview.emergency_reports > 0 && (
                      <div className="flex justify-between">
                        <span>Emergency Reports:</span>
                        <Badge variant="destructive">
                          {preview.emergency_reports}
                        </Badge>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-destructive font-semibold">
                  ⚠️ This includes all users created by this admin, and all
                  users created by those users (cascading), and all their data.
                </p>

                <p className="text-sm text-muted-foreground">
                  Email: {userEmail}
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
              <Button
                variant="destructive"
                onClick={() => setStep("confirm")}
                disabled={!preview}
              >
                Continue to Confirmation
              </Button>
            </AlertDialogFooter>
          </>
        )}

        {step === "confirm" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Final Confirmation Required
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left space-y-4">
                <p className="font-bold text-destructive">
                  ⚠️ THIS ACTION IS IRREVERSIBLE ⚠️
                </p>

                <p>
                  You are about to permanently delete <strong>{userName}</strong> and
                  all associated data. This cannot be undone.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="confirm-delete">
                    Type <span className="font-mono font-bold">delete</span> to
                    confirm:
                  </Label>
                  <Input
                    id="confirm-delete"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="delete"
                    className="font-mono"
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={confirmText !== "delete" || isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Permanently"}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
